import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel, camelToSnake } from '../lib/utils';
import { logAction } from '../lib/actionLogger';

function mapFromDb(row) { return snakeToCamel(row); }
function mapToDb(obj) { return camelToSnake(obj); }

export function useLegalDocs() {
  return useQuery({
    queryKey: queryKeys.legalDocs.all,
    queryFn: async () => {
      const data = await api.get('/legal-docs');
      return data.map(mapFromDb);
    },
  });
}

export function useLegalDoc(id) {
  return useQuery({
    queryKey: queryKeys.legalDocs.detail(id),
    queryFn: async () => {
      const data = await api.get(`/legal-docs/${id}`);
      return mapFromDb(data);
    },
    enabled: !!id,
  });
}

export function useCreateLegalDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (newDoc) => {
      const data = await api.post('/legal-docs', mapToDb(newDoc));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.legalDocs.all });
      logAction({
        action: 'Create Legal Doc',
        details: { title: data.title, type: data.type, status: data.status },
      });
    },
  });
}

export function useUpdateLegalDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (doc) => {
      const { id, ...updates } = doc;
      const data = await api.put(`/legal-docs/${id}`, mapToDb(updates));
      return mapFromDb(data);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.legalDocs.all });
      qc.invalidateQueries({ queryKey: queryKeys.legalDocs.detail(data.id) });
      logAction({
        action: 'Update Legal Doc',
        details: { title: data.title, status: data.status },
      });
    },
  });
}

export function useDeleteLegalDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId) => {
      await api.del(`/legal-docs/${docId}`);
    },
    onSuccess: (_data, docId) => {
      qc.invalidateQueries({ queryKey: queryKeys.legalDocs.all });
      logAction({
        action: 'Delete Legal Doc',
        details: { docId },
      });
    },
  });
}
